import { Editor, Range, Node } from 'vendor/slate'
import { getSelectionPlainText } from 'cg/components/documents/editor/ai/serializeSelection'

/** Locate selectionText in blockText using a hint — never global indexOf from 0. */
export const relocateSelectionInBlockText = (blockText, selectionText, startHint = 0) => {
  if (!blockText || !selectionText) return null

  const hint = Math.max(startHint ?? 0, 0)
  if (blockText.slice(hint, hint + selectionText.length) === selectionText) {
    return { selectionStart: hint, selectionEnd: hint + selectionText.length }
  }

  const idx = blockText.indexOf(selectionText, Math.max(hint - 5, 0))
  if (idx >= 0) {
    return { selectionStart: idx, selectionEnd: idx + selectionText.length }
  }

  return null
}

/** 1-based block range for the chat badge (like Cursor line numbers). */
export const formatBlockRange = (blockStart, blockEnd, { isPartialSelection, selectionStart, selectionEnd } = {}) => {
  if (blockStart == null || blockEnd == null) return null
  const from = blockStart + 1
  const to = blockEnd + 1

  if (isPartialSelection && from === to && selectionStart != null && selectionEnd != null) {
    return `${from}·${selectionStart + 1}–${selectionEnd}`
  }

  return from === to ? String(from) : `${from}–${to}`
}

export const buildDocumentChatContext = (editor, meta, { selection: selectionOverride } = {}) => {
  const {
    documentId,
    versionId,
    sectionId,
    workspaceId,
    sectionTitle
  } = meta

  const base = {
    documentId,
    versionId,
    sectionId,
    workspaceId,
    sectionTitle: sectionTitle || 'Section'
  }

  const selection = selectionOverride ?? editor.selection
  if (!selection) {
    return {
      ...base,
      blockIndex: null,
      blockIndexEnd: null,
      blockType: null,
      blockText: null,
      selectionText: null,
      selectionStart: null,
      selectionEnd: null,
      isPartialSelection: false,
      textPreview: null,
      isExpanded: false,
      isMultiBlock: false,
      selectionPath: null,
      badgeRange: null
    }
  }

  const anchorBlock = selection.anchor.path[0]
  const focusBlock = selection.focus.path[0]
  const blockStart = Math.min(anchorBlock, focusBlock)
  const blockEnd = Math.max(anchorBlock, focusBlock)
  const isMultiBlock = blockEnd > blockStart
  const isExpanded = !Range.isCollapsed(selection)
  const [selectionStartPoint] = Range.edges(selection)
  // Path relative to read_document_block's slateNode root (strip top-level section index).
  const selectionPath =
    selectionStartPoint.path[0] === blockStart ? selectionStartPoint.path.slice(1) : []
  const block = editor.children[blockStart]
  const blockType = block?.type || null
  let blockText = block ? Node.string(block) : null

  let selectionText = null
  let textPreview = null
  let selectionStart = null
  let selectionEnd = null
  let isPartialSelection = false

  if (isExpanded) {
    selectionText = getSelectionPlainText(editor, selection).slice(0, 2000)
    textPreview = selectionText.slice(0, 500)

    if (!isMultiBlock && blockText && selectionText) {
      const blockPath = [blockStart]
      const blockStartPoint = Editor.start(editor, blockPath)
      const [start, end] = Range.edges(selection)
      const beforeRange = { anchor: blockStartPoint, focus: start }
      const rangeLen = Editor.string(editor, { anchor: start, focus: end }).length
      selectionStart = Editor.string(editor, beforeRange).length
      selectionEnd = selectionStart + rangeLen

      // Fallback when inline marks make Editor.string offsets disagree with plain text.
      // Search near the Slate-derived hint — never global indexOf (duplicate words).
      const relocated = relocateSelectionInBlockText(blockText, selectionText, selectionStart)
      if (relocated) {
        selectionStart = relocated.selectionStart
        selectionEnd = relocated.selectionEnd
      }

      isPartialSelection = selectionStart > 0 || selectionEnd < blockText.length
    } else if (isMultiBlock && selectionText) {
      // Multi-block spans cannot be patched by single-block offsets — locate-only.
      blockText = null
      selectionStart = null
      selectionEnd = null
      isPartialSelection = false
    }
  }

  if (blockType === 'raw_markdown' && isPartialSelection) {
    blockText = null
  }

  return {
    ...base,
    blockIndex: blockStart,
    blockIndexEnd: blockEnd,
    blockType,
    blockText,
    selectionText,
    selectionStart,
    selectionEnd,
    isPartialSelection,
    textPreview,
    isExpanded,
    isMultiBlock,
    selectionPath,
    badgeRange: formatBlockRange(blockStart, blockEnd, { isPartialSelection, selectionStart, selectionEnd })
  }
}
